
import { Component, Prop, Vue } from 'vue-property-decorator';
import { CalendarEventParticipantResponse } from '@/../types/enums';
import { CalendarEventParticipant } from '@/../types/interfaces';
import { TeamModel, PlayerOnTeam } from '../../models/team';
import { StaffMember } from '../../models/team/StaffMember';
import { UserProfileModel } from '../../models/UserProfileModel';

@Component
export default class TeamMemberSelector extends Vue{

	@Prop({ type: Boolean, default: false }) disabled: boolean;
	@Prop({ type: Boolean, default: false }) loading: boolean;
	@Prop({ type: Boolean, default: true }) usePlayers: boolean;
	@Prop({ type: Boolean, default: true }) useStaff: boolean;
	@Prop({ type: Number, default: 550 }) maxHeight: number;
	@Prop({ default: () => [] }) participants: CalendarEventParticipant[];

	@Prop({ required: true }) team: TeamModel;

	get MemberSelectorStyle(): Record<string, string>{
		return {
			'overflow-y': 'auto',
			'max-height': `${this.maxHeight}px`,
		};
	}

	get Selected(): CalendarEventParticipant[]{
		return this.participants ?? [];
	}

	/**
	 * A member is on the event when a participant carries their id. We can't compare whole
	 * participant objects: the ones already on the event carry an rsvp, a note and a response
	 * time, so they never match a freshly built one.
	 */
	IsSelected(memberId: string): boolean{
		return this.Selected.some(p => p.id === memberId);
	}

	/** Emits a new list, never mutates the prop, and never lets the same member in twice. */
	private emitParticipants(participants: CalendarEventParticipant[]): void{
		const seen = new Set<string>();
		const deduped = participants.filter(p => {
			if(seen.has(p.id)) return false;
			seen.add(p.id);
			return true;
		});
		this.$emit('update:participants', deduped);
	}

	/** Adds the member if they're missing, removes them if they're already on the event. */
	toggleMember(participant: CalendarEventParticipant): void{
		if(this.IsSelected(participant.id)){
			this.emitParticipants(this.Selected.filter(p => p.id !== participant.id));
		}else{
			this.emitParticipants([...this.Selected, participant]);
		}
	}

	playerToUserInfo(player: PlayerOnTeam): Partial<UserProfileModel>{
		return {
			id: player.athleteId,
			firstName: player.firstName,
			lastName: player.lastName,
		}
	}
	staffToUserInfo(staff: StaffMember): Partial<UserProfileModel>{
		return {
			id: staff.coachId,
			firstName: staff.firstName,
			lastName: staff.lastName,
		}
	}

	playerToParticipant(player: PlayerOnTeam): CalendarEventParticipant{
		return {
			id: player.id,
			name: `${player.firstName} ${player.lastName}`,
			email: player.email,
			userId: player.userId,
			athleteId: player.athleteId,
			coachId: null,
			rsvp: CalendarEventParticipantResponse.None,
			// Left unset: a status only appears once a coach records attendance.
			tardiness: null
		};
	}
	staffToParticipant(staff: StaffMember): CalendarEventParticipant{
		return {
			id: staff.id,
			name: `${staff.firstName} ${staff.lastName}`,
			email: staff.email,
			userId: staff.userId,
			athleteId: null,
			coachId: staff.coachId,
			rsvp: CalendarEventParticipantResponse.None,
			tardiness: null
		};
	}

	get Players(): PlayerOnTeam[]{
		if(!this.team) return [];
		return this.team.players ?? [];
	}
	get Staff(): StaffMember[]{
		if(!this.team) return [];
		return this.team.staffMembers ?? [];
	}

	get SelectedPlayerCount(): number{
		return this.Players.filter(p => this.IsSelected(p.id)).length;
	}
	get SelectedStaffCount(): number{
		return this.Staff.filter(s => this.IsSelected(s.id)).length;
	}

	get AllPlayersSelected(): boolean{
		return this.Players.length > 0 && this.SelectedPlayerCount === this.Players.length;
	}
	get SomePlayersSelected(): boolean{
		return this.SelectedPlayerCount > 0 && !this.AllPlayersSelected;
	}
	toggleSelectAllPlayers(): void{
		if(this.AllPlayersSelected){
			const playerIds = new Set(this.Players.map(p => p.id));
			this.emitParticipants(this.Selected.filter(p => !playerIds.has(p.id)));
		}else{
			const missing = this.Players
				.filter(p => !this.IsSelected(p.id))
				.map(p => this.playerToParticipant(p));
			this.emitParticipants([...this.Selected, ...missing]);
		}
	}

	get AllStaffSelected(): boolean{
		return this.Staff.length > 0 && this.SelectedStaffCount === this.Staff.length;
	}
	get SomeStaffSelected(): boolean{
		return this.SelectedStaffCount > 0 && !this.AllStaffSelected;
	}
	toggleSelectAllStaff(): void{
		if(this.AllStaffSelected){
			const staffIds = new Set(this.Staff.map(s => s.id));
			this.emitParticipants(this.Selected.filter(p => !staffIds.has(p.id)));
		}else{
			const missing = this.Staff
				.filter(s => !this.IsSelected(s.id))
				.map(s => this.staffToParticipant(s));
			this.emitParticipants([...this.Selected, ...missing]);
		}
	}

}
